import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Plus, X, Save, Sparkles, Loader2, Users, ArrowRight } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import { useModels } from '../hooks/ModelsContext';
import { RepresentativenessDashboard } from './RepresentativenessDashboard';

export function PanelBuilder({ library }) {
  const { t } = useTranslation();
  const { models: MODELS, resolveModel } = useModels();
  const [saveName, setSaveName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [panelSearch, setPanelSearch] = useState('');
  const [modelAssignments, setModelAssignments] = useState({});

  const modelIds = Object.keys(MODELS);
  const getModelForPersona = (personaId, index) => {
    if (modelAssignments[personaId]) return modelAssignments[personaId];
    const persona = library.panelPersonas.find(p => p.id === personaId);
    if (persona?.default_model) {
      const resolved = resolveModel(persona.default_model);
      if (MODELS[resolved]) return resolved;
    }
    return modelIds[index % modelIds.length];
  };
  const setModelForPersona = (personaId, modelId) => setModelAssignments(prev => ({ ...prev, [personaId]: modelId }));

  const filteredPersonas = library.libraryPersonas.filter(p =>
    !panelSearch || p.name.toLowerCase().includes(panelSearch.toLowerCase())
  );

  const handleSavePanel = async () => {
    if (!saveName.trim()) return;
    await library.savePanel(saveName);
    setShowSaveDialog(false);
    setSaveName('');
  };

  const handleFillGaps = async () => {
    const result = await library.fillGaps();
    const suggestions = result?.suggestions || result || [];
    if (Array.isArray(suggestions) && suggestions.length > 0) {
      for (const s of suggestions) {
        if (s.id) library.addToPanel(s.id);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Saved panels bar */}
      {library.panels.length > 0 && (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
          <div className="flex items-center gap-3 overflow-x-auto">
            <span className="text-sm font-medium text-stone-500 flex-shrink-0">{t('personaLib.savedPanels')}:</span>
            {library.panels.map(panel => (
              <button
                key={panel.id}
                onClick={() => library.loadPanel(panel.id)}
                className="flex-shrink-0 px-3 py-1.5 bg-stone-100 hover:bg-amber-100 rounded-lg text-sm transition-colors"
              >
                {panel.name} ({panel.persona_ids?.length || 0})
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Persona picker */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
          <h3 className="font-serif text-xl mb-4">{t('personaLib.availablePersonas')}</h3>
          <div className="relative mb-4">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              value={panelSearch}
              onChange={e => setPanelSearch(e.target.value)}
              placeholder={t('personaLib.searchPersonas')}
              className="input pl-10 w-full"
            />
          </div>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {filteredPersonas.map(persona => {
              const isInPanel = library.currentPanel.persona_ids.includes(persona.id);
              return (
                <div key={persona.id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isInPanel ? 'border-amber-300 bg-amber-50' : 'border-stone-100 hover:bg-stone-50'}`}>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                    {persona.name?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{persona.name}</div>
                    <div className="text-xs text-stone-500">{persona.age} {t('persona.years')}{persona.region ? `, ${persona.region}` : ''}</div>
                  </div>
                  <button
                    onClick={() => isInPanel ? library.removeFromPanel(persona.id) : library.addToPanel(persona.id)}
                    className={`p-1.5 rounded-lg transition-colors ${isInPanel ? 'text-red-500 hover:bg-red-50' : 'text-amber-600 hover:bg-amber-50'}`}
                  >
                    {isInPanel ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </button>
                </div>
              );
            })}
            {filteredPersonas.length === 0 && (
              <div className="text-center py-8 text-stone-400 text-sm">{t('personaLib.noPersonasFound')}</div>
            )}
          </div>
        </div>

        {/* Right: Current panel */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl">{t('personaLib.currentPanel')} ({library.currentPanel.persona_ids.length})</h3>
              <div className="flex gap-2">
                <button onClick={() => setShowSaveDialog(true)} disabled={library.currentPanel.persona_ids.length === 0} className="btn btn-secondary text-sm flex items-center gap-1">
                  <Save className="w-3 h-3" /> {t('personaLib.savePanel')}
                </button>
              </div>
            </div>

            {library.panelPersonas.length === 0 ? (
              <div className="text-center py-8 text-stone-400">
                <Users className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                <p className="text-sm">{t('personaLib.emptyPanel')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {library.panelPersonas.map((persona, idx) => {
                  const assignedModelId = getModelForPersona(persona.id, idx);
                  const assignedModel = MODELS[assignedModelId] || { name: assignedModelId, color: '#888' };
                  return (
                    <div key={persona.id} className="flex items-center gap-3 p-2 rounded-lg bg-stone-50">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                        {persona.name?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium">{persona.name}</span>
                        <span className="text-xs text-stone-400 ml-2">{persona.age}{persona.gender ? `, ${persona.gender}` : ''}</span>
                      </div>
                      <select
                        value={assignedModelId}
                        onChange={(e) => setModelForPersona(persona.id, e.target.value)}
                        className="text-xs rounded-full px-2 py-1 text-white border-0 cursor-pointer"
                        style={{ backgroundColor: assignedModel.color }}
                      >
                        {Object.entries(MODELS).map(([id, m]) => (
                          <option key={id} value={id} style={{ color: '#000', backgroundColor: '#fff' }}>{m.name}</option>
                        ))}
                      </select>
                      <button onClick={() => library.removeFromPanel(persona.id)} className="text-red-400 hover:text-red-600 p-1">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Smart Fill & Use buttons */}
            <div className="flex gap-2 mt-4 pt-4 border-t border-stone-100">
              <button
                onClick={handleFillGaps}
                disabled={library.isAiWorking || library.currentPanel.persona_ids.length === 0}
                className="btn btn-secondary flex items-center gap-2 flex-1"
              >
                {library.isAiWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {t('personaLib.smartFill')}
              </button>
            </div>
          </div>

          {/* Representativeness Dashboard */}
          {library.representativeness && (
            <RepresentativenessDashboard
              data={library.representativeness}
              benchmarks={library.benchmarks}
              selectedBenchmark={library.selectedBenchmark}
              onBenchmarkChange={library.setSelectedBenchmark}
            />
          )}
        </div>
      </div>

      {/* Save Panel Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSaveDialog(false)}>
          <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-lg mb-3">{t('personaLib.savePanelTitle')}</h3>
            <input type="text" value={saveName} onChange={e => setSaveName(e.target.value)} placeholder={t('personaLib.panelNamePlaceholder')} className="input w-full mb-4" autoFocus />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowSaveDialog(false)} className="btn btn-secondary">{t('personaLib.cancel')}</button>
              <button onClick={handleSavePanel} disabled={!saveName.trim()} className="btn btn-primary">{t('personaLib.save')}</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
